import React, { useState, useEffect } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { useParams } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    LineController
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    LineController
);

function StatsTab() {
    const [clinicStats, setClinicStats] = useState({});
    const { clinicId } = useParams();

    useEffect(() => {
        clinicService.getPatientStats(clinicId)
            .then(res => {
                setClinicStats(res.data);
                console.log("Patient Stats", res.data);
            })
            .catch(err => {
                console.log(err);
            });
    }, []);

    // Helper function to format numbers with K, M suffixes
    const formatNumber = (num) => {
        if (!num) return '0';

        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toLocaleString();
    };

    // Helper function to display full number in tooltips but formatted in UI
    const formatNumberForDisplay = (num) => {
        if (!num) return '0';
        return num.toLocaleString();
    };

    // Helper function to calculate percentages for progress bars
    const calculatePercentage = (value, total) => {
        return total > 0 ? (value / total) * 100 : 0;
    };

    // Prepare data for the age group chart
    const ageGroupChartData = {
        labels: ['Children', 'Teenager', 'Adult', 'Senior Citizen', 'Other'],
        datasets: [
            {
                label: 'Number of Patients',
                data: [
                    clinicStats.ageGroup?.children || 0,
                    clinicStats.ageGroup?.teenager || 0,
                    clinicStats.ageGroup?.adult || 0,
                    clinicStats.ageGroup?.seniorCitizen || 0,
                    clinicStats.ageGroup?.other || 0
                ],
                backgroundColor: [
                    'rgba(54, 162, 235, 0.5)',
                    'rgba(75, 192, 192, 0.5)',
                    'rgba(153, 102, 255, 0.5)',
                    'rgba(255, 159, 64, 0.5)',
                    'rgba(201, 203, 207, 0.5)'
                ],
                borderColor: [
                    'rgb(54, 162, 235)',
                    'rgb(75, 192, 192)',
                    'rgb(153, 102, 255)',
                    'rgb(255, 159, 64)',
                    'rgb(201, 203, 207)'
                ],
                borderWidth: 1,
            },
        ],
    };

    // Communication Preferences Pie Chart
    const commPrefChartData = {
        labels: ['Email', 'Cell Phone', 'Home Phone'],
        datasets: [
            {
                data: [
                    clinicStats.commPref?.patientWithEmail || 0,
                    clinicStats.commPref?.patientWithCellPhone || 0,
                    clinicStats.commPref?.patientWithHomePhone || 0
                ],
                backgroundColor: [
                    'rgba(54, 162, 235, 0.7)',
                    'rgba(75, 192, 192, 0.7)',
                    'rgba(255, 205, 86, 0.7)',
                ],
                borderColor: [
                    'rgb(54, 162, 235)',
                    'rgb(75, 192, 192)',
                    'rgb(255, 205, 86)',
                ],
                borderWidth: 1,
            },
        ],
    };

    // Patient Status Distribution
    const patientStatusData = {
        labels: ['Active', 'Inactive'],
        datasets: [
            {
                label: 'Patients',
                data: [
                    clinicStats.patient?.activePatient || 0,
                    clinicStats.patient?.inactivePatient || 0
                ],
                backgroundColor: [
                    'rgba(40, 167, 69, 0.7)',
                    'rgba(108, 117, 125, 0.7)'
                ],
                borderColor: [
                    'rgb(40, 167, 69)',
                    'rgb(108, 117, 125)'
                ],
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Age Group Distribution',
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `${context.dataset.label}: ${formatNumberForDisplay(context.raw)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    callback: function(value) {
                        return formatNumber(value);
                    }
                }
            }
        },
        maintainAspectRatio: false,
    };

    const pieChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'bottom',
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${formatNumberForDisplay(value)} (${percentage}%)`;
                    }
                }
            }
        },
        maintainAspectRatio: false,
    };

    return (
        <div className="container-fluid p-0">
            <div className="card card-padding shadow-none">
                <div className="card-title mb-3">
                    <i className="bi bi-bar-chart"></i> Patient Stats
                </div>

                {/* Patient Summary Cards */}
                <div className="row">
                    <div className="col-12 col-sm-4 mb-3 mb-sm-0">
                        <div className="card card-border shadow-none">
                            <div className="card-body py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="card-title mb-0">Total Patients</h6>
                                        <h4 className="mb-0 mt-1">{formatNumber(clinicStats.patient?.totalPatient || 0)}</h4>
                                        <small className="text-muted">{formatNumberForDisplay(clinicStats.patient?.totalPatient || 0)} total</small>
                                    </div>
                                    <div className="bg-primary bg-opacity-10 p-3 rounded">
                                        <i className="bi bi-people-fill text-primary"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-sm-4 mb-3 mb-sm-0">
                        <div className="card card-border shadow-none">
                            <div className="card-body py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="card-title mb-0">Active Patients</h6>
                                        <h4 className="mb-0 mt-1">{formatNumber(clinicStats.patient?.activePatient || 0)}</h4>
                                        <small className="text-muted">{formatNumberForDisplay(clinicStats.patient?.activePatient || 0)} active</small>
                                    </div>
                                    <div className="bg-success bg-opacity-10 p-3 rounded">
                                        <i className="bi bi-person-check-fill text-success"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-sm-4">
                        <div className="card card-border shadow-none">
                            <div className="card-body py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="card-title mb-0">Inactive Patients</h6>
                                        <h4 className="mb-0 mt-1">{formatNumber(clinicStats.patient?.inactivePatient || 0)}</h4>
                                        <small className="text-muted">{formatNumberForDisplay(clinicStats.patient?.inactivePatient || 0)} inactive</small>
                                    </div>
                                    <div className="bg-secondary bg-opacity-10 p-3 rounded">
                                        <i className="bi bi-person-dash-fill text-secondary"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* New Charts Row */}
                <div className="row mt-3">
                    {/* Communication Preferences Pie Chart */}
                    <div className="col-12 col-lg-6 mb-3">
                        <div className="card card-border shadow-none h-100">
                            <div className="card-body">
                                <h6 className="card-title mb-3">Communication Preferences</h6>
                                <div style={{ height: '250px' }}>
                                    <Pie data={commPrefChartData} options={pieChartOptions} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Patient Status Distribution */}
                    <div className="col-12 col-lg-6 mb-3">
                        <div className="card card-border shadow-none h-100">
                            <div className="card-body">
                                <h6 className="card-title mb-3">Patient Status Distribution</h6>
                                <div style={{ height: '250px' }}>
                                    <Pie data={patientStatusData} options={pieChartOptions} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Communication Preferences Progress Bars */}
                <div className="card card-border shadow-none mt-3">
                    <div className="card-body">
                        <h6 className="card-title mb-3">Communication Preferences Details</h6>

                        <div className="row">
                            <div className="col-12 col-sm-6 mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span><i className="bi bi-envelope"></i> Email</span>
                                    <span>{formatNumber(clinicStats.commPref?.patientWithEmail || 0)}/{formatNumber(clinicStats.patient?.totalPatient || 0)}</span>
                                </div>
                                <div className="progress" style={{ height: "8px" }}>
                                    <div
                                        className="progress-bar bg-info"
                                        role="progressbar"
                                        style={{ width: `${calculatePercentage(clinicStats.commPref?.patientWithEmail || 0, clinicStats.patient?.totalPatient || 1)}%` }}
                                        aria-valuenow={clinicStats.commPref?.patientWithEmail || 0}
                                        aria-valuemin="0"
                                        aria-valuemax={clinicStats.patient?.totalPatient || 1}
                                    ></div>
                                </div>
                                <small className="text-muted">
                                    {formatNumberForDisplay(clinicStats.commPref?.patientWithEmail || 0)} of {formatNumberForDisplay(clinicStats.patient?.totalPatient || 0)}
                                </small>
                            </div>

                            <div className="col-12 col-sm-6 mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span><i className="bi bi-phone"></i>Cell Phone</span>
                                    <span>{formatNumber(clinicStats.commPref?.patientWithCellPhone || 0)}/{formatNumber(clinicStats.patient?.totalPatient || 0)}</span>
                                </div>
                                <div className="progress" style={{ height: "8px" }}>
                                    <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{ width: `${calculatePercentage(clinicStats.commPref?.patientWithCellPhone || 0, clinicStats.patient?.totalPatient || 1)}%` }}
                                        aria-valuenow={clinicStats.commPref?.patientWithCellPhone || 0}
                                        aria-valuemin="0"
                                        aria-valuemax={clinicStats.patient?.totalPatient || 1}
                                    ></div>
                                </div>
                                <small className="text-muted">
                                    {formatNumberForDisplay(clinicStats.commPref?.patientWithCellPhone || 0)} of {formatNumberForDisplay(clinicStats.patient?.totalPatient || 0)}
                                </small>
                            </div>

                            <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                                <div className="d-flex justify-content-between mb-1">
                                    <span><i className="bi bi-telephone"></i> Home Phone</span>
                                    <span>{formatNumber(clinicStats.commPref?.patientWithHomePhone || 0)}/{formatNumber(clinicStats.patient?.totalPatient || 0)}</span>
                                </div>
                                <div className="progress" style={{ height: "8px" }}>
                                    <div
                                        className="progress-bar bg-warning"
                                        role="progressbar"
                                        style={{ width: `${calculatePercentage(clinicStats.commPref?.patientWithHomePhone || 0, clinicStats.patient?.totalPatient || 1)}%` }}
                                        aria-valuenow={clinicStats.commPref?.patientWithHomePhone || 0}
                                        aria-valuemin="0"
                                        aria-valuemax={clinicStats.patient?.totalPatient || 1}
                                    ></div>
                                </div>
                                <small className="text-muted">
                                    {formatNumberForDisplay(clinicStats.commPref?.patientWithHomePhone || 0)} of {formatNumberForDisplay(clinicStats.patient?.totalPatient || 0)}
                                </small>
                            </div>

                            <div className="col-12 col-sm-6">
                                <div className="d-flex justify-content-between mb-1">
                                    <span><i className="bi bi-send"></i> Welcome Sent</span>
                                    <span>{formatNumber(clinicStats.commPref?.welcomeSent || 0)}/{formatNumber(clinicStats.patient?.totalPatient || 0)}</span>
                                </div>
                                <div className="progress" style={{ height: "8px" }}>
                                    <div
                                        className="progress-bar bg-primary"
                                        role="progressbar"
                                        style={{ width: `${calculatePercentage(clinicStats.commPref?.welcomeSent || 0, clinicStats.patient?.totalPatient || 1)}%` }}
                                        aria-valuenow={clinicStats.commPref?.welcomeSent || 0}
                                        aria-valuemin="0"
                                        aria-valuemax={clinicStats.patient?.totalPatient || 1}
                                    ></div>
                                </div>
                                <small className="text-muted">
                                    {formatNumberForDisplay(clinicStats.commPref?.welcomeSent || 0)} of {formatNumberForDisplay(clinicStats.patient?.totalPatient || 0)}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Age Group Distribution Chart */}
                <div className="card card-border shadow-none mt-3">
                    <div className="card-body">
                        <h6 className="card-title mb-3">Age Group Distribution Chart</h6>
                        <div style={{ height: '300px' }}>
                            <Bar options={chartOptions} data={ageGroupChartData} />
                        </div>
                    </div>
                </div>

                {/* Age Group Distribution Icons */}
                <div className="card card-border shadow-none mt-3">
                    <div className="card-body">
                        <h6 className="card-title mb-3">Age Group Distribution</h6>

                        <div className="row">
                            <div className="col-6 col-sm-4 col-lg-2 mb-3">
                                <div className="text-center">
                                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-flex">
                                        <i className="bi bi-emoji-smile-fill text-primary"></i>
                                    </div>
                                    <h6 className="mt-2 mb-0">Children</h6>
                                    <p className="mb-0">{formatNumber(clinicStats.ageGroup?.children || 0)}</p>
                                    <small className="text-muted">{formatNumberForDisplay(clinicStats.ageGroup?.children || 0)}</small>
                                </div>
                            </div>

                            <div className="col-6 col-sm-4 col-lg-2 mb-3">
                                <div className="text-center">
                                    <div className="bg-info bg-opacity-10 p-3 rounded-circle d-inline-flex">
                                        <i className="bi bi-emoji-laughing-fill text-info"></i>
                                    </div>
                                    <h6 className="mt-2 mb-0">Teenager</h6>
                                    <p className="mb-0">{formatNumber(clinicStats.ageGroup?.teenager || 0)}</p>
                                    <small className="text-muted">{formatNumberForDisplay(clinicStats.ageGroup?.teenager || 0)}</small>
                                </div>
                            </div>

                            <div className="col-6 col-sm-4 col-lg-2 mb-3">
                                <div className="text-center">
                                    <div className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-flex">
                                        <i className="bi bi-person-fill text-success"></i>
                                    </div>
                                    <h6 className="mt-2 mb-0">Adult</h6>
                                    <p className="mb-0">{formatNumber(clinicStats.ageGroup?.adult || 0)}</p>
                                    <small className="text-muted">{formatNumberForDisplay(clinicStats.ageGroup?.adult || 0)}</small>
                                </div>
                            </div>

                            <div className="col-6 col-sm-4 col-lg-2 mb-3">
                                <div className="text-center">
                                    <div className="bg-warning bg-opacity-10 p-3 rounded-circle d-inline-flex">
                                        <i className="bi bi-person-badge-fill text-warning"></i>
                                    </div>
                                    <h6 className="mt-2 mb-0">Senior</h6>
                                    <p className="mb-0">{formatNumber(clinicStats.ageGroup?.seniorCitizen || 0)}</p>
                                    <small className="text-muted">{formatNumberForDisplay(clinicStats.ageGroup?.seniorCitizen || 0)}</small>
                                </div>
                            </div>

                            <div className="col-6 col-sm-4 col-lg-2 mb-3">
                                <div className="text-center">
                                    <div className="bg-secondary bg-opacity-10 p-3 rounded-circle d-inline-flex">
                                        <i className="bi bi-question-circle-fill text-secondary"></i>
                                    </div>
                                    <h6 className="mt-2 mb-0">Other</h6>
                                    <p className="mb-0">{formatNumber(clinicStats.ageGroup?.other || 0)}</p>
                                    <small className="text-muted">{formatNumberForDisplay(clinicStats.ageGroup?.other || 0)}</small>
                                </div>
                            </div>

                            <div className="col-6 col-sm-4 col-lg-2 mb-3">
                                <div className="text-center">
                                    <div className="bg-dark bg-opacity-10 p-3 rounded-circle d-inline-flex">
                                        <i className="bi bi-pie-chart-fill text-dark"></i>
                                    </div>
                                    <h6 className="mt-2 mb-0">Total</h6>
                                    <p className="mb-0">{formatNumber(clinicStats.patient?.totalPatient || 0)}</p>
                                    <small className="text-muted">{formatNumberForDisplay(clinicStats.patient?.totalPatient || 0)}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StatsTab;